import { createContext } from 'react'
import {
  createRouter,
  createRoute,
  createRootRoute,
  RouterProvider,
  Outlet,
  Navigate,
} from '@tanstack/react-router'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useTheme } from './hooks/useTheme'
import Dashboard from './components/Dashboard'
import CustomerList from './components/CustomerList'
import Analytics from './components/Analytics'
import Inventory from './components/Inventory'
import AlertRules from './components/AlertRules'
import SystemHealth from './components/SystemHealth'
import Onboarding from './components/Onboarding'
import PipelineInjector from './components/PipelineInjector'
import Navigation from './components/Navigation'
import Login from './components/Login'

export const ThemeContext = createContext({ dark: false, toggle: () => {} })

// ─── Root layout (auth gate + shell) ────────────────────────────────────────

function RootLayout() {
  const { accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const theme = useTheme()

  if (inProgress === 'startup' || inProgress === 'handleRedirect') {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated) return <Login />

  return (
    <ThemeContext.Provider value={theme}>
      <div className="app">
        <Navigation user={accounts[0]} />
        <div className="page-container">
          <main className="main-content">
            <Outlet />
          </main>
        </div>
      </div>
    </ThemeContext.Provider>
  )
}

// ─── Route tree ──────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({ component: RootLayout })

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Dashboard,
})

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomerList,
})

const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: Analytics,
})

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  validateSearch: (search) => ({
    filter: (search.filter ?? 'all'),
  }),
  component: Inventory,
})

const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts',
  component: AlertRules,
})

const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/health',
  component: SystemHealth,
})

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: Onboarding,
})

const pipelineInjectorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pipeline-injector',
  component: PipelineInjector,
})

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: () => <Navigate to="/" />,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  customersRoute,
  analyticsRoute,
  inventoryRoute,
  alertsRoute,
  healthRoute,
  onboardingRoute,
  pipelineInjectorRoute,
  catchAllRoute,
])

const router = createRouter({ routeTree })

export default function App() {
  return <RouterProvider router={router} />
}
